/**
 * Vuex Store for OAUTH client (applications) settings
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2018
 */

import { ApplicationsApi } from '@/services/api-applications.js';
import { collectionUnchanged } from '@/utils/helpers.js';

function defaultStateFactory() {
  return {
    applications: [],
    pagination: {},
    application: {},
  };
}

export const state = defaultStateFactory();

export const getters = {
  application: (state) => state.application,

  applicationById: (state) => (id) => state.applications.find((application) => application.id === id),

  applicationIndex: (state) => (id) => state.applications.findIndex((application) => application.id === id),
};

export const mutations = {
  SET_APPLICATIONS(state, { meta = {}, data = [] }) {
    state.pagination = meta;
    state.applications = data;
  },

  SET_APPLICATION(state, application = {}) {
    state.application = application;
  },

  ADD_APPLICATION(state, application = {}) {
    state.applications.push(application);
  },

  DELETE_APPLICATION(state, { index = -1 }) {
    state.application = {};
    // remove from the collection if it exists
    if (index > -1) {
      state.applications.splice(index, 1);
    }
  },

  RESET_STATE(state, defaultState) {
    state = defaultState;
  },
};

export const actions = {
  fetch({ commit, state }, { query }) {
    return ApplicationsApi.getApps(query)
      .then((applications) => {
        if (!collectionUnchanged(state.applications, applications.data, ['secret'])) {
          commit('SET_APPLICATIONS', applications);
        }
        return state.applications;
      })
      .catch((err) => {
        commit('SET_APPLICATIONS', {});
        throw err;
      });
  },

  create({ commit }, { data }) {
    return ApplicationsApi.createApp(data).then((application) => {
      commit('ADD_APPLICATION', application);
      return application;
    });
  },

  read({ commit, getters, state }, { id }) {
    // do we already have the app in the list
    const application = getters.applicationById(id);
    if (application) {
      commit('SET_APPLICATION', application);
      return Promise.resolve(application);
    }

    // if not fetch it from the server
    return ApplicationsApi.getApp(id)
      .then((application) => {
        commit('SET_APPLICATION', application);
        return state.application;
      })
      .catch((err) => {
        commit('SET_APPLICATION', {});
        throw err;
      });
  },

  update({ commit, state, rootGetters }, { id, data }) {
    return ApplicationsApi.updateApp(id, data).then((application) => {
      // copy over the existing secret key to the newly updated app
      if (application && state.application.secret && rootGetters['auth/can']('read-application-key')) {
        application.secret = state.application.secret;
      }
      commit('SET_APPLICATION', application);
      return state.application;
    });
  },

  destroy({ commit, getters }, { id }) {
    return ApplicationsApi.deleteApp(id).then((application) => {
      commit('DELETE_APPLICATION', {
        index: getters.applicationIndex(id),
      });
      return application;
    });
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('RESET_STATE', defaultStateFactory());
  },
};
